import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Mail, Phone, MessageCircle, Users as UsersIcon, StickyNote, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  Email: { icon: Mail, color: "text-violet-600 bg-violet-50 border-violet-200" },
  Call: { icon: Phone, color: "text-sky-600 bg-sky-50 border-sky-200" },
  WhatsApp: { icon: MessageCircle, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  Meeting: { icon: UsersIcon, color: "text-amber-600 bg-amber-50 border-amber-200" },
  Note: { icon: StickyNote, color: "text-stone-600 bg-stone-50 border-stone-200" },
};

const EMPTY = { type: "Call", direction: "Outgoing", subject: "", details: "", contact: "" };

export default function CommsTab({ projectId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [confirmId, setConfirmId] = useState("");
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      setItems((await api.get(`/projects/${projectId}/communications`)).data);
    } catch { toast.error("Failed to load communications"); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/communications`, form);
      toast.success("Communication logged");
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (cid) => {
    if (confirmId !== cid) { setConfirmId(cid); setTimeout(() => setConfirmId(""), 3000); return; }
    try {
      await api.delete(`/projects/${projectId}/communications/${cid}`);
      toast.success("Entry deleted");
      load();
    } catch { toast.error("Delete failed"); }
    finally { setConfirmId(""); }
  };

  return (
    <div className="space-y-4" data-testid="comms-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-stone-600">Full client communication history — emails are logged automatically.</div>
        <button onClick={() => setShowForm(!showForm)} data-testid="comms-add-btn"
          className="flex items-center gap-1.5 bg-stone-900 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
          <Plus className="w-3.5 h-3.5" /> Log Communication
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-stone-200 rounded-xl p-4 grid grid-cols-2 gap-3" data-testid="comms-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="comms-type"
              className="w-full mt-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg">
              {["Call", "WhatsApp", "Meeting", "Email", "Note"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Direction</label>
            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="comms-direction"
              className="w-full mt-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg">
              <option>Outgoing</option><option>Incoming</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Subject / Summary</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required data-testid="comms-subject"
              className="w-full mt-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg" placeholder="e.g. Discussed pending MPCB query documents" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Contact Person</label>
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="comms-contact"
              className="w-full mt-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Details</label>
            <input value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} data-testid="comms-details"
              className="w-full mt-1 px-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg" />
          </div>
          <button type="submit" data-testid="comms-submit" className="col-span-2 bg-emerald-700 text-white py-2 rounded-lg text-sm font-medium">Save Entry</button>
        </form>
      )}

      <div className="space-y-2">
        {items.map((c) => {
          const meta = TYPE_META[c.type] || TYPE_META.Note;
          const Icon = meta.icon;
          return (
            <div key={c.id} className="bg-white border border-stone-200 rounded-xl p-3.5 flex gap-3 items-start" data-testid={`comm-${c.id}`}>
              <div className={`p-2 rounded-lg border ${meta.color} shrink-0`}><Icon className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-stone-900">{c.subject}</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 font-bold">{c.type} · {c.direction}</span>
                  {c.auto && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold">Auto-logged</span>}
                </div>
                {c.details && <div className="text-xs text-stone-600 mt-0.5">{c.details}</div>}
                <div className="text-[10px] text-stone-400 mt-1">
                  {c.at ? new Date(c.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                  {c.logged_by_name ? ` · ${c.logged_by_name}` : ""}
                  {c.contact ? ` · with ${c.contact}` : ""}
                </div>
              </div>
              {!c.auto && (
                <button onClick={() => remove(c.id)} data-testid={`comm-delete-${c.id}`}
                  className={`p-1.5 rounded transition ${confirmId === c.id ? "text-white bg-rose-600 text-[10px] font-bold px-2" : "text-stone-500 hover:text-rose-600 hover:bg-rose-50"}`}>
                  {confirmId === c.id ? "Confirm?" : <Trash2 className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          );
        })}
        {items.length === 0 && <div className="text-center py-10 text-sm text-stone-400">No communications logged yet</div>}
      </div>
    </div>
  );
}
